import React from 'react';
import styled, { keyframes } from 'styled-components';

// Spinner animation
const spin = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
`;

// Styled components
const SpinnerContainer = styled.div<{ $size?: 'small' | 'medium' | 'large' }>`
    display: inline-block;
    width: ${props => {
        switch (props.$size) {
            case 'small': return '16px';
            case 'large': return '32px';
            default: return '24px';
        }
    }};
    height: ${props => {
        switch (props.$size) {
            case 'small': return '16px';
            case 'large': return '32px';
            default: return '24px';
        }
    }};
    border: 2px solid #f3f3f3;
    border-top: 2px solid #007bff;
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
`;

const PulseContainer = styled.div<{ $size?: 'small' | 'medium' | 'large' }>`
    display: inline-block;
    width: ${props => {
        switch (props.$size) {
            case 'small': return '16px';
            case 'large': return '32px';
            default: return '24px';
        }
    }};
    height: ${props => {
        switch (props.$size) {
            case 'small': return '16px';
            case 'large': return '32px';
            default: return '24px';
        }
    }};
    background: #007bff;
    border-radius: 50%;
    animation: ${pulse} 1.5s ease-in-out infinite;
`;

const LoadingOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    border-radius: inherit;
`;

const LoadingText = styled.div`
    color: #666;
    font-size: 14px;
    margin-top: 8px;
    text-align: center;
`;

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
`;

// Main LoadingSpinner component
interface LoadingSpinnerProps {
    size?: 'small' | 'medium' | 'large';
    variant?: 'spinner' | 'pulse';
    text?: string;
    overlay?: boolean;
    className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
    size = 'medium',
    variant = 'spinner',
    text,
    overlay = false,
    className
}) => {
    const spinner = variant === 'pulse' ? (
        <PulseContainer $size={size} />
    ) : (
        <SpinnerContainer $size={size} />
    );

    if (overlay) {
        return (
            <LoadingOverlay className={className}>
                <LoadingContainer>
                    {spinner}
                    {text && <LoadingText>{text}</LoadingText>}
                </LoadingContainer>
            </LoadingOverlay>
        );
    }

    return (
        <LoadingContainer className={className}>
            {spinner}
            {text && <LoadingText>{text}</LoadingText>}
        </LoadingContainer>
    );
};

// Gallery-specific loading components
export const GalleryLoadingSpinner: React.FC<{ text?: string }> = ({ text = 'Loading...' }) => (
    <LoadingSpinner size="large" text={text} />
);

export const ImageLoadingSpinner: React.FC<{ overlay?: boolean }> = ({ overlay = true }) => (
    <LoadingSpinner size="medium" variant="pulse" overlay={overlay} text="Loading image..." />
);

export const NavigationLoadingSpinner: React.FC = () => (
    <LoadingSpinner size="small" />
);
